import { useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronsLeft, ChevronsRight } from 'lucide-react'
import { navigation, type NavGroup, type NavItem } from '@/app/navigation'
import { useAccess } from '@/shared/rbac'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useSidebarStore } from '@/shared/stores/sidebar-store'
import { useAnnouncementsUnreadCount } from '@/features/announcements/hooks/use-announcements'

const SCROLL_KEY = 'sidebar-scroll'

export function Sidebar() {
  const navRef = useRef<HTMLElement>(null)
  const location = useLocation()
  const access = useAccess()
  const isLoading = useAuthStore((s) => s.isLoading)
  const isSuperadmin = useAuthStore((s) => s.hasPermission3d('system_admin', 'edit', 'all'))
  const { collapsed, toggleCollapsed, setCollapsed } = useSidebarStore()
  // Unread announcements → small dot on the Announcements nav item. The query
  // self-gates on canViewAnnouncements, so it's a no-op for users without the module.
  const { data: unread } = useAnnouncementsUnreadCount()
  const hasUnreadAnnouncements = (unread?.total ?? 0) > 0

  // Restore scroll position before paint to avoid visible jump
  useLayoutEffect(() => {
    const saved = sessionStorage.getItem(SCROLL_KEY)
    if (saved && navRef.current) navRef.current.scrollTop = Number(saved)
  }, [])

  const handleScroll = useCallback(() => {
    if (navRef.current) sessionStorage.setItem(SCROLL_KEY, String(navRef.current.scrollTop))
  }, [])

  // Visibility: prefer the post-refactor `module` field (drives off the BE
  // module catalog via useAccess().can). Fall back to the legacy named gate
  // for items that haven't been migrated yet, or that need composite logic.
  const filteredGroups = navigation
    .map((group) => ({
      ...group,
      items: group.items.filter((item: NavItem) => {
        if (item.superadminOnly && !isSuperadmin) return false
        if (item.module) return access.can(item.module, 'read')
        if (item.access) return access[item.access]
        return true
      }),
    }))
    .filter((group) => group.items.length > 0)

  return (
    <aside className={`sidebar${collapsed ? ' collapsed' : ''}`}>
      {/* Logo */}
      <div className="sidebar-logo">
        <Link
          to="/workspace/queue"
          onClick={() => {
            if (collapsed) setCollapsed(false)
          }}
          aria-label="Home"
          style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
        >
          <img
            src="/brand/medicodio-icon.png"
            alt=""
            aria-hidden="true"
            className="logo-icon"
            style={{
              background: 'transparent',
              boxShadow: 'none',
              objectFit: 'contain',
              cursor: 'pointer',
            }}
          />
        </Link>
        <Link
          to="/workspace/queue"
          aria-label="Medicodio home"
          style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
        >
          <img
            src="/brand/medicodio-wordmark-blue.png"
            alt="Medicodio"
            className="logo-text"
            style={{ height: 32, width: 'auto', display: 'block', cursor: 'pointer' }}
          />
        </Link>
      </div>

      {/* Navigation */}
      <nav ref={navRef} className="sidebar-nav" aria-label="Main navigation" onScroll={handleScroll}>
        {filteredGroups.map((group) => (
          <NavGroupSection
            key={group.label}
            group={group}
            currentPath={location.pathname}
            collapsed={collapsed}
            disabled={isLoading}
            hasUnreadAnnouncements={hasUnreadAnnouncements}
          />
        ))}
      </nav>

      {/* Collapse toggle */}
      <button
        className="sidebar-collapse-btn"
        onClick={toggleCollapsed}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
      </button>
    </aside>
  )
}

function NavGroupSection({
  group,
  currentPath,
  collapsed,
  disabled,
  hasUnreadAnnouncements,
}: {
  group: NavGroup
  currentPath: string
  collapsed: boolean
  disabled: boolean
  hasUnreadAnnouncements: boolean
}) {
  return (
    <div className="nav-group">
      {!collapsed && <div className="nav-group-label">{group.label}</div>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {group.items.map((item) => (
          <NavItemLink
            key={item.path}
            item={item}
            currentPath={currentPath}
            collapsed={collapsed}
            disabled={disabled}
            showDot={!!item.unreadDot && hasUnreadAnnouncements}
          />
        ))}
      </ul>
    </div>
  )
}

function NavItemLink({
  item,
  currentPath,
  collapsed,
  disabled,
  showDot,
}: {
  item: NavItem
  currentPath: string
  collapsed: boolean
  disabled: boolean
  showDot: boolean
}) {
  const isActive =
    currentPath === item.path || currentPath.startsWith(item.path + '/')
  const Icon = item.icon

  return (
    <li>
      <Link
        to={item.path}
        className={`nav-link${isActive ? ' active' : ''}${showDot ? ' has-dot' : ''}`}
        aria-current={isActive ? 'page' : undefined}
        data-tooltip={collapsed ? item.label : undefined}
        aria-disabled={disabled || undefined}
        onClick={disabled ? (e) => e.preventDefault() : undefined}
        style={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
        tabIndex={disabled ? -1 : undefined}
      >
        <span className="nav-icon">
          <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
        </span>
        <span>{item.label}</span>
        {showDot && (
          <span
            className="nav-unread-dot"
            aria-label="New announcements"
            role="status"
          />
        )}
      </Link>
    </li>
  )
}
